// src/components/SubmitScreen.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { locationData } from "../locationData";

import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import "./SubmitScreen.css";

function buildLocationKey(border, state, county, city) {
  if (border === "National") return "National";
  const parts = [border];
  if (state) parts.push(state);
  if (county) parts.push(county);
  if (city) parts.push(city);
  return parts.join("|");
}

function parseTags(tagString) {
  if (!tagString) return [];
  const raw = tagString
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  return Array.from(new Set(raw));
}

export default function SubmitScreen() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [dateOfSubmission, setDateOfSubmission] = useState("");
  const [description, setDescription] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [tags, setTags] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [privacyApproved, setPrivacyApproved] = useState(false);

  const [selectedBorder, setSelectedBorder] = useState("National");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const states = useMemo(() => {
    if (selectedBorder === "National") return [];
    return Object.keys(locationData?.[selectedBorder]?.states || {});
  }, [selectedBorder]);

  const counties = useMemo(() => {
    if (selectedBorder === "National" || !selectedState) return [];
    return Object.keys(
      locationData?.[selectedBorder]?.states?.[selectedState]?.counties || {}
    );
  }, [selectedBorder, selectedState]);

  const cities = useMemo(() => {
    if (selectedBorder === "National" || !selectedState || !selectedCounty) return [];
    return (
      locationData?.[selectedBorder]?.states?.[selectedState]?.counties?.[selectedCounty] || []
    );
  }, [selectedBorder, selectedState, selectedCounty]);

  const handleBorderChange = (e) => {
    const v = e.target.value;
    setSelectedBorder(v);
    setSelectedState("");
    setSelectedCounty("");
    setSelectedCity("");
  };

  const handleStateChange = (e) => {
    const v = e.target.value;
    setSelectedState(v);
    setSelectedCounty("");
    setSelectedCity("");
  };

  const handleCountyChange = (e) => {
    const v = e.target.value;
    setSelectedCounty(v);
    setSelectedCity("");
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");

    if (!user) return setErr("You must be logged in to submit.");
    if (!privacyApproved) return setErr("You must approve the Privacy Rights and Disclaimer.");

    if (
      !title.trim() ||
      !description.trim() ||
      !dateOfSubmission ||
      !email.trim() ||
      !phone.trim()
    ) {
      return setErr("Please fill all required fields.");
    }

    setSubmitting(true);

    try {
      const tagArray = parseTags(tags);

      const locationKey = buildLocationKey(
        selectedBorder,
        selectedState,
        selectedCounty,
        selectedCity
      );

      const locationLabel =
        selectedBorder === "National"
          ? "National"
          : selectedCity || selectedCounty || selectedState || selectedBorder;

      await addDoc(collection(db, "posts"), {
        title: title.trim(),
        description: description.trim(),
        dateOfSubmission,
        mediaUrl: (mediaUrl || "").trim(),
        mediaStoragePath: null,

        tags: tagArray,
        topicTags: tagArray, // adjust if you want separate topic field

        border: selectedBorder,
        state: selectedState || "",
        county: selectedCounty || "",
        city: selectedCity || "",
        location: locationLabel,
        locationKey,

        authorId: user.uid,
        authorEmail: user.email || "",
        contactEmail: email.trim(),
        contactPhone: phone.trim(),

        approved: false,
        createdAt: serverTimestamp(),
      });

      setOk("Submitted successfully! Your post is pending approval.");

      setTitle("");
      setDateOfSubmission("");
      setDescription("");
      setMediaUrl("");
      setTags("");
      setPhone("");
      setPrivacyApproved(false);
    } catch (error) {
      console.error(error);
      setErr(error.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submit-container">
      <h1>Submit Your Journalistic Work</h1>

      {err && <p className="error-text">{err}</p>}
      {ok && <p className="ok-text">{ok}</p>}

      <form className="submit-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Article or Video Name *"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <input
          type="date"
          value={dateOfSubmission}
          onChange={(e) => setDateOfSubmission(e.target.value)}
          required
        />

        <textarea
          placeholder="Description *"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          rows={5}
        />

        <div className="two-col">
          <input
            type="url"
            placeholder="Image or Video URL (optional)"
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
          />

          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
          />
        </div>

        <div className="location-box">
          <h3>Location Scope</h3>

          <div className="filter-row">
            <label>Border:</label>
            <select value={selectedBorder} onChange={handleBorderChange}>
              <option value="National">National</option>
              <option value="Mexico Border">Mexico Border</option>
              <option value="Canada Border">Canada Border</option>
            </select>
          </div>

          {selectedBorder !== "National" && (
            <div className="filter-row">
              <label>State:</label>
              <select value={selectedState} onChange={handleStateChange}>
                <option value="">-- Select State --</option>
                {states.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedState && (
            <div className="filter-row">
              <label>County:</label>
              <select value={selectedCounty} onChange={handleCountyChange}>
                <option value="">-- Select County --</option>
                {counties.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedCounty && (
            <div className="filter-row">
              <label>City:</label>
              <select value={selectedCity} onChange={handleCityChange}>
                <option value="">-- Select City --</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="checkbox-row">
          <label>
            <input
              type="checkbox"
              checked={privacyApproved}
              onChange={(e) => setPrivacyApproved(e.target.checked)}
              required
            />
            I approve the Privacy Rights and Disclaimer *
          </label>
        </div>

        <div className="two-col">
          <input
            type="email"
            placeholder="Email Address *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            type="tel"
            placeholder="Phone Number *"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
        </div>

        <div className="actions-row">
          <button className="btn primary" type="submit" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit"}
          </button>

          <button className="btn ghost" type="button" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </form>
    </div>
  );
}
